import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaTimes } from 'react-icons/fa';

import { useLazySearchBuildingsQuery, SearchBuildingItem } from '@client/api';
import { Input } from '@client/toolkit';

import { BrregCompanyData, BrregSearchResponse } from '../../../companies/hooks/useBrregCompanySearch';
import { BuildingForm, BuildingFormData } from '../components/building-form/building.form';
import { OnboardingBuilding } from '../../onboarding/onboarding.types';
import { OnboardingActions } from '../../onboarding/onboarding-actions';

const KARTVERKET_API_URL = 'https://ws.geonorge.no/adresser/v1/sok';
const BRREG_API_URL = 'https://data.brreg.no/enhetsregisteret/api/enheter';

type SearchTab = 'address' | 'company' | 'manual';

type TypeaheadItem =
  | { kind: 'address'; data: KartverketAddress }
  | { kind: 'company'; data: BrregCompanyData };

function formatBrregAddress(company: BrregCompanyData): string {
  const addr = company.forretningsadresse;
  if (!addr) return '';
  const parts = [
    addr.adresse?.join(', '),
    addr.postnummer,
    addr.poststed,
  ].filter(Boolean);
  return parts.join(', ');
}

interface KartverketAddress {
  adressetekst: string;
  kommunenummer: string;
  kommunenavn: string;
  gardsnummer: number;
  bruksnummer: number;
  postnummer: string;
  poststed: string;
  representasjonspunkt?: { lat: number; lon: number };
}

interface KartverketResponse {
  adresser: KartverketAddress[];
}

interface StepByggProps {
  company: BrregCompanyData | null;
  buildings: OnboardingBuilding[];
  onBuildingsChange: (buildings: OnboardingBuilding[]) => void;
  onBack: () => void;
  onNext: () => void;
}

const BUILDING_TYPE_LABELS: Record<string, string> = {
  Office: 'Kontor',
  RetailHighStreet: 'Handel High Street',
  Retail: 'Handel',
  Logistics: 'Logistikk',
  Industrial: 'Industri',
  Hotel: 'Hotell',
  Healthcare: 'Helse',
  Education: 'Undervisning',
  Mixed: 'Kombinasjon',
};

const ENERGY_LABEL_RANK: Record<string, number> = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7 };

function groupBuildingsByMatrikkel(buildings: OnboardingBuilding[]): OnboardingBuilding[] {
  const groups = new Map<string, OnboardingBuilding[]>();
  for (const b of buildings) {
    if (b.municipalityCode && b.gnr != null && b.bnr != null) {
      const key = `${b.municipalityCode}-${b.gnr}/${b.bnr}`;
      groups.set(key, [...(groups.get(key) ?? []), b]);
    } else {
      groups.set(b.id, [b]);
    }
  }

  return Array.from(groups.entries()).map(([key, items]) => {
    if (items.length === 1) return items[0];

    const totalBra = items.reduce((sum, b) => sum + b.bra, 0);
    const maxFloors = Math.max(...items.map((b) => b.numberOfFloors ?? 0));
    const buildingNumbers = items.map((b) => b.buildingNumber).filter(Boolean) as string[];

    const worstEnergy = items
      .filter((b) => b.energyLabel && ENERGY_LABEL_RANK[b.energyLabel])
      .sort((a, b) => (ENERGY_LABEL_RANK[b.energyLabel!] ?? 0) - (ENERGY_LABEL_RANK[a.energyLabel!] ?? 0))[0]
      ?.energyLabel;

    const worstHeating = items
      .filter((b) => b.heatingGrade)
      .sort((a, b) => (a.heatingGrade ?? '').localeCompare(b.heatingGrade ?? ''))[0]?.heatingGrade;

    const types = new Set(items.map((b) => b.type));
    const type = types.size === 1 ? items[0].type : 'Mixed';

    const largest = items.reduce((a, b) => (b.bra > a.bra ? b : a), items[0]);

    return {
      ...largest,
      id: `matrikkel-${key}`,
      bra: totalBra,
      type,
      numberOfFloors: maxFloors || undefined,
      energyLabel: worstEnergy,
      heatingGrade: worstHeating,
      buildingNumber: buildingNumbers.join(', '),
      isGrouped: true,
      buildingNumbers,
      childBuildings: items,
      selected: items.some((b) => b.selected),
    };
  });
}

function mapSearchResultToBuilding(item: SearchBuildingItem): OnboardingBuilding {
  return {
    id: `pp-${item.buildingNumber ?? crypto.randomUUID()}`,
    name: item.address ?? undefined,
    address: item.address ?? '',
    postalCode: item.postalCode ?? '',
    city: item.city ?? '',
    type: item.buildingType ?? 'Mixed',
    bra: item.utilityFloorSpace ?? item.grossArea ?? 0,
    selected: false,
    role: '',
    source: 'placepoint',
    buildingNumber: item.buildingNumber ?? undefined,
    municipalityCode: item.municipalityCode ?? undefined,
    // The search API returns numeric gnr/bnr; the wizard carries them as strings so manual
    // entries can hold non-numeric foreign cadastral identifiers alongside.
    gnr: item.gnr != null ? String(item.gnr) : undefined,
    bnr: item.bnr != null ? String(item.bnr) : undefined,
    numberOfFloors: item.numberOfFloors ?? undefined,
    energySourceType: item.energySourceType ?? undefined,
    heatingType: item.heatingType ?? undefined,
    matrikkelBuildingTypeCode: item.matrikkelBuildingTypeCode ?? undefined,
    latitude: item.latitude ?? undefined,
    longitude: item.longitude ?? undefined,
    ownerOrgNumber: item.ownerOrgNumber ?? undefined,
    ownerName: item.ownerName ?? undefined,
    owners: item.owners?.map((o) => ({
      name: o.name ?? undefined,
      organizationNumber: o.organizationNumber ?? undefined,
      isJuridical: o.isJuridical,
      shareNumerator: o.shareNumerator ?? undefined,
      shareDenominator: o.shareDenominator ?? undefined,
    })),
    ownerPath: item.ownerPath ?? undefined,
    ownerPathRootOrgNumber: item.ownerPathRootOrgNumber ?? undefined,
    energyLabel: item.energyLabel ?? undefined,
    heatingGrade: item.heatingGrade ?? undefined,
    constructionYear: item.constructionYear ?? undefined,
    energyCertificateUrl: item.energyCertificateUrl ?? undefined,
    energyCertificateNumber: item.energyCertificateNumber ?? undefined,
    energyCertificateIssuedDate: item.energyCertificateIssuedDate ?? undefined,
  };
}


export function StepBygg({ company, buildings, onBuildingsChange, onBack, onNext }: StepByggProps) {
  const { t } = useTranslation('portfolio');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTab, setSearchTab] = useState<SearchTab>('address');
  const [showManualForm, setShowManualForm] = useState(false);
  const [searchResults, setSearchResults] = useState<OnboardingBuilding[]>(
    () => buildings.filter((b) => b.source === 'placepoint'),
  );
  const [hasSearched, setHasSearched] = useState(
    () => buildings.some((b) => b.source === 'placepoint'),
  );
  const [lastSearchLabel, setLastSearchLabel] = useState('');
  const [isMatrikkelGrouped, setIsMatrikkelGrouped] = useState(true);
  const [isFetchingBuildings, setIsFetchingBuildings] = useState(false);
  const [isSlowSearch, setIsSlowSearch] = useState(false);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Typeahead state
  const [typeaheadItems, setTypeaheadItems] = useState<TypeaheadItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isTypeaheadLoading, setIsTypeaheadLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [triggerSearch, { error: searchError }] = useLazySearchBuildingsQuery();

  // Manual buildings persisted in parent state
  const manualBuildings = buildings.filter((b) => b.source === 'manual');

  // Search results with selection state from parent
  const rawSearchResults = searchResults
    .map((sr) => {
      const existing = buildings.find((b) => b.id === sr.id);
      return existing ? { ...sr, selected: existing.selected, role: existing.role } : sr;
    });

  const displaySearchResults = isMatrikkelGrouped
    ? groupBuildingsByMatrikkel(rawSearchResults)
    : rawSearchResults;

  const allBuildings = [...displaySearchResults, ...manualBuildings];
  const selectedCount = allBuildings.filter((b) => b.selected).length;

  // Auto-search on mount when company is available
  useEffect(() => {
    if (company?.organisasjonsnummer && !hasSearched) {
      setLastSearchLabel(company.navn ?? company.organisasjonsnummer);
      handleBuildingSearch(company.organisasjonsnummer, 'orgNr');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.organisasjonsnummer]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Typeahead: debounced search while typing
  // Address tab → Kartverket | Company tab → Brreg
  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 2) {
      setTypeaheadItems([]);
      setShowDropdown(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setIsTypeaheadLoading(true);
      try {
        if (searchTab === 'address') {
          const res = await fetch(`${KARTVERKET_API_URL}?sok=${encodeURIComponent(query)}&fuzzy=true&treffPerSide=8`);
          if (!res.ok) return;
          const data: KartverketResponse = await res.json();
          const items: TypeaheadItem[] = data.adresser.map((a) => ({ kind: 'address', data: a }));
          setTypeaheadItems(items);
          setShowDropdown(items.length > 0);
        } else {
          const isOrgNr = /^\d{9}$/.test(query.replace(/\s/g, ''));
          let companies: BrregCompanyData[] = [];

          if (isOrgNr) {
            const orgNr = query.replace(/\s/g, '');
            const res = await fetch(`${BRREG_API_URL}/${orgNr}`);
            if (res.ok) {
              const single: BrregCompanyData = await res.json();
              companies = [single];
            }
          } else {
            const res = await fetch(`${BRREG_API_URL}?navn=${encodeURIComponent(query)}&size=8`);
            if (res.ok) {
              const data: BrregSearchResponse = await res.json();
              companies = data._embedded?.enheter ?? [];
              const queryLower = query.toLowerCase();
              companies.sort((a, b) => {
                const aStarts = a.navn.toLowerCase().startsWith(queryLower) ? 0 : 1;
                const bStarts = b.navn.toLowerCase().startsWith(queryLower) ? 0 : 1;
                return aStarts - bStarts;
              });
            }
          }

          const items: TypeaheadItem[] = companies.map((c) => ({ kind: 'company', data: c }));
          setTypeaheadItems(items);
          setShowDropdown(items.length > 0);
        }
        setHighlightedIndex(-1);
      } catch {
        setTypeaheadItems([]);
        setShowDropdown(false);
      } finally {
        setIsTypeaheadLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, searchTab]);

  // Search buildings via backend (PlacePoint) — replaces unchecked, keeps checked
  const handleBuildingSearch = async (query: string, type: 'orgNr' | 'auto') => {
    if (!query.trim()) return;

    setShowDropdown(false);
    setTypeaheadItems([]);
    setIsFetchingBuildings(true);
    setIsSlowSearch(false);
    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    slowTimerRef.current = setTimeout(() => setIsSlowSearch(true), 4000);

    try {
      const result = await triggerSearch({ query: query.trim(), searchType: type }).unwrap();
      setHasSearched(true);

      const seen = new Set<string>();
      const mapped = result.buildings
        .map(mapSearchResultToBuilding)
        .filter((b) => {
          if (seen.has(b.id)) return false;
          seen.add(b.id);
          return true;
        });
      const newIds = new Set(mapped.map((b) => b.id));

      // Keep previously selected that aren't duplicated in new results
      const selectedIds = new Set(buildings.filter((b) => b.source === 'placepoint' && b.selected).map((b) => b.id));
      const keptFromPrevious = searchResults.filter((b) => selectedIds.has(b.id) && !newIds.has(b.id));
      const newBuildings = mapped.map((b) => selectedIds.has(b.id) ? { ...b, selected: true } : b);
      setSearchResults([...keptFromPrevious, ...newBuildings]);

      const manualOnly = buildings.filter((b) => b.source === 'manual');
      const keptSelected = buildings.filter((b) => b.source === 'placepoint' && b.selected && !newIds.has(b.id));
      onBuildingsChange([...manualOnly, ...keptSelected, ...newBuildings]);
    } catch {
      // error handled via searchError
    } finally {
      setIsFetchingBuildings(false);
      setIsSlowSearch(false);
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showDropdown && typeaheadItems.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, typeaheadItems.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault();
        handleTypeaheadPick(typeaheadItems[highlightedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setShowDropdown(false);
        setHighlightedIndex(-1);
        return;
      }
    }
  };

  const handleTypeaheadPick = (item: TypeaheadItem) => {
    setShowDropdown(false);
    setTypeaheadItems([]);
    setSearchQuery('');
    if (item.kind === 'address') {
      const a = item.data;
      setLastSearchLabel(a.adressetekst);
      const cadastreQuery = `${a.kommunenummer}-${a.gardsnummer}/${a.bruksnummer}`;
      handleBuildingSearch(cadastreQuery, 'auto');
    } else {
      setLastSearchLabel(item.data.navn);
      handleBuildingSearch(item.data.organisasjonsnummer, 'orgNr');
    }
  };

  const handleToggle = (id: string) => {
    const building = displaySearchResults.find((b) => b.id === id);
    if (building?.isGrouped && building.buildingNumbers) {
      // Toggle all underlying buildings by matrikkel match
      const matrikkelKey = `${building.municipalityCode}-${building.gnr}/${building.bnr}`;
      const newSelected = !building.selected;
      const updatedRaw = rawSearchResults.map((b) => {
        const bKey = b.municipalityCode && b.gnr != null && b.bnr != null
          ? `${b.municipalityCode}-${b.gnr}/${b.bnr}` : '';
        return bKey === matrikkelKey ? { ...b, selected: newSelected } : b;
      });
      onBuildingsChange([...updatedRaw, ...manualBuildings]);
    } else {
      // Always toggle on raw individual buildings to preserve grouped selection state
      const updatedRaw = rawSearchResults.map((b) =>
        b.id === id ? { ...b, selected: !b.selected } : b,
      );
      onBuildingsChange([...updatedRaw, ...manualBuildings]);
    }
  };

  const handleSelectAll = () => {
    const allSelected = displaySearchResults.every((b) => b.selected);
    if (isMatrikkelGrouped) {
      // Toggle all underlying raw results
      const updatedRaw = rawSearchResults.map((b) => ({ ...b, selected: !allSelected }));
      onBuildingsChange([...updatedRaw, ...manualBuildings]);
    } else {
      const updatedSearch = displaySearchResults.map((b) => ({ ...b, selected: !allSelected }));
      onBuildingsChange([...updatedSearch, ...manualBuildings]);
    }
  };

  const handleRemoveManual = (id: string) => {
    onBuildingsChange(buildings.filter((b) => b.id !== id));
  };

  const handleManualBuildingSubmit = useCallback(
    async (data: BuildingFormData) => {
      const address = data.address;
      const streetName = address?.streetName ?? '';
      const streetNumber = address?.streetNumber ?? '';
      const fullAddress = streetNumber ? `${streetName} ${streetNumber}` : streetName;

      // The manual form stores the cadastral parts in the PlacePoint slot convention
      // (cadastralUnit=kommunenr, propertyUnit=gnr, leaseholdUnit=bnr — see formatMatrikkel);
      // map them back onto the flat wizard fields the bulk payload carries.
      const cadastral = data.cadastralInformation;

      const newBuilding: OnboardingBuilding = {
        id: `manual-${crypto.randomUUID()}`,
        name: data.name || undefined,
        address: fullAddress,
        streetName: streetName || undefined,
        streetNumber: streetNumber || undefined,
        postalCode: address?.zipCode ?? '',
        city: (address?.zipPlace ?? '').toUpperCase(),
        countryThreeLetterCode: address?.country?.threeLetterCode ?? undefined,
        type: data.type ?? 'Mixed',
        bra: data.areaMetric ?? 0,
        energyLabel: data.energyLabel ?? undefined,
        heatingGrade: data.heatingGrade ?? undefined,
        latitude: address?.coordinate?.latitude ?? undefined,
        longitude: address?.coordinate?.longitude ?? undefined,
        municipalityCode: cadastral?.cadastralUnit?.trim() || undefined,
        gnr: cadastral?.propertyUnit?.trim() || undefined,
        bnr: cadastral?.leaseholdUnit?.trim() || undefined,
        buildingNumber: data.buildingNumber?.trim() || undefined,
        selected: true,
        role: '',
        source: 'manual',
      };

      onBuildingsChange([...buildings, newBuilding]);
      setShowManualForm(false);
    },
    [buildings, onBuildingsChange],
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Bygg i porteføljen</h2>
      <p className="text-white/60 mb-8">
        Velg bygg fra søkeresultater eller registrer manuelt.
      </p>

      {/* Search tabs */}
      <div className="flex gap-1 mb-4 bg-surface-elevated rounded-lg p-1">
        {([
          { value: 'address' as SearchTab, label: 'Adresse' },
          { value: 'company' as SearchTab, label: 'Selskapsnavn' },
          { value: 'manual' as SearchTab, label: t('onboarding.buildings.manualTab') },
        ]).map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => {
              setSearchTab(tab.value);
              setSearchQuery('');
              setTypeaheadItems([]);
              setShowDropdown(false);
              // The manual tab keeps search results untouched — switching over to register a
              // building by hand must not discard what a search already found.
              if (tab.value === 'manual') return;
              // Clear unchecked results, keep only selected
              const kept = searchResults.filter((b) => buildings.some((eb) => eb.id === b.id && eb.selected));
              setSearchResults(kept);
              const manualOnly = buildings.filter((b) => b.source === 'manual');
              const selectedPlacepoint = buildings.filter((b) => b.source === 'placepoint' && b.selected);
              onBuildingsChange([...manualOnly, ...selectedPlacepoint]);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              searchTab === tab.value
                ? 'bg-brand-primary text-black'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search bar */}
      {searchTab !== 'manual' && (
      <div className="relative mb-6" ref={dropdownRef}>
        <div className="flex gap-3 items-stretch">
          <div className="flex-1">
            <Input
              ref={inputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (typeaheadItems.length > 0) setShowDropdown(true); }}
              placeholder={searchTab === 'address' ? 'Søk etter gateadresse...' : 'Søk etter selskapsnavn...'}
              className="!h-full"
            />
          </div>
          {isTypeaheadLoading && (
            <div className="flex items-center px-3">
              <div className="w-5 h-5 border-2 border-white/30 border-t-brand-primary rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Typeahead dropdown */}
        {showDropdown && typeaheadItems.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-border bg-surface-base shadow-lg max-h-80 overflow-y-auto">
            {typeaheadItems.map((item, index) => (
              <button
                key={item.kind === 'address'
                  ? `${item.data.kommunenummer}-${item.data.gardsnummer}/${item.data.bruksnummer}-${item.data.adressetekst}`
                  : item.data.organisasjonsnummer}
                type="button"
                onClick={() => handleTypeaheadPick(item)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`w-full text-left px-4 py-3 border-b border-border/50 last:border-b-0 transition-colors ${
                  index === highlightedIndex
                    ? 'bg-surface-hover'
                    : 'hover:bg-surface-hover'
                }`}
              >
                {item.kind === 'address' ? (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-white text-sm truncate">{item.data.adressetekst}</div>
                      <div className="text-white/40 text-xs mt-0.5 truncate">
                        {item.data.poststed} - {item.data.kommunenummer}-{item.data.gardsnummer}/{item.data.bruksnummer}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-white text-sm truncate">{item.data.navn}</div>
                      <div className="text-white/40 text-xs mt-0.5 truncate">
                        {formatBrregAddress(item.data)}
                      </div>
                    </div>
                    <div className="text-white/40 text-xs whitespace-nowrap pt-0.5">
                      {item.data.organisasjonsnummer}
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {searchTab !== 'manual' && searchError && (
        <p className="text-red-400 text-sm mb-4">Søket feilet. Prøv igjen.</p>
      )}

      {/* Section 1: Search Results */}
      {searchTab !== 'manual' && displaySearchResults.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">
              Søkeresultater{lastSearchLabel && <span className="text-white/50 font-normal"> — {lastSearchLabel}</span>}
            </h3>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setIsMatrikkelGrouped(!isMatrikkelGrouped)}
                className="text-brand-primary text-sm font-medium hover:underline"
              >
                {isMatrikkelGrouped ? 'Vis enkeltbygg' : 'Grupper matrikkel'}
              </button>
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-brand-primary text-sm font-medium hover:underline"
              >
                {displaySearchResults.every((b) => b.selected) ? 'Fjern alle' : 'Velg alle'}
              </button>
              <span className="text-white/50 text-sm">
                {displaySearchResults.filter((b) => b.selected).length} av {displaySearchResults.length} valgt
              </span>
            </div>
          </div>

          <div
            className="max-h-[540px] overflow-y-auto pr-2 space-y-3 rounded-lg [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#322F2C] [&::-webkit-scrollbar-thumb]:rounded"
            role="listbox"
            aria-label="Søkeresultater"
          >
            {displaySearchResults.map((building) => (
              <div
                key={building.id}
                className={`p-5 rounded-lg border transition-all cursor-pointer ${
                  building.selected
                    ? 'border-brand-primary bg-surface-selected'
                    : 'border-border bg-surface-base hover:bg-surface-hover'
                }`}
                onClick={() => handleToggle(building.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-6 h-6 shrink-0 rounded flex items-center justify-center mt-0.5 border-2 transition-all ${
                        building.selected
                          ? 'bg-brand-primary border-brand-primary'
                          : 'border-white/30 bg-transparent'
                      }`}
                    >
                      {building.selected && (
                        <svg className="w-3.5 h-3.5 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="font-bold text-white">
                        {building.address || 'Ukjent adresse'}{building.postalCode ? `, ${building.postalCode}` : ''} {building.city}
                      </div>
                      <div className="text-white/50 text-sm mt-1 flex flex-wrap gap-3">
                        {building.bra > 0 && <span>BRA: {building.bra.toLocaleString('nb-NO')} m²</span>}
                        {building.municipalityCode && building.gnr != null && building.bnr != null && (
                          <span>Matrikkel: {building.municipalityCode}-{building.gnr}/{building.bnr}</span>
                        )}
                        {building.buildingNumber && <span>Byggnr: {building.buildingNumber}</span>}
                        {building.energyLabel && <span>Energi: {building.energyLabel}</span>}
                        {building.constructionYear && <span>Byggeår: {building.constructionYear}</span>}
                        {building.isGrouped && building.buildingNumbers && (
                          <span className="text-white/30">({building.buildingNumbers.length} bygg gruppert)</span>
                        )}
                        {/* {building.heatingGrade && <span>Oppvarming: {building.heatingGrade}</span>} */}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs px-3 py-1 rounded bg-surface-elevated text-white/70 font-medium whitespace-nowrap">
                      {BUILDING_TYPE_LABELS[building.type] ?? building.type}
                    </span>
                    {building.isGrouped && (
                      <span className="text-xs px-3 py-1 rounded bg-amber-500/20 text-amber-400 font-medium whitespace-nowrap">
                        Gruppert
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {searchTab !== 'manual' && isFetchingBuildings && (
        <div className="flex flex-col items-center justify-center py-12 mb-6">
          <div className="w-10 h-10 border-2 border-brand-primary rounded-full animate-spin border-r-transparent mb-3" />
          <p className="text-white/50 text-sm">
            {isSlowSearch ? 'Vi finner veldig mange bygg...vennligst vent' : 'Søker etter bygg...'}
          </p>
        </div>
      )}

      {searchTab !== 'manual' && hasSearched && searchResults.length === 0 && !isFetchingBuildings && (
        <p className="text-white/50 text-sm mb-6">Ingen bygg funnet for søket.</p>
      )}

      {/* Section 2: Manually Added Buildings */}
      {searchTab === 'manual' && <div>
        <h3 className="text-lg font-semibold text-white mb-4">{t('onboarding.buildings.manualHeading')}</h3>

        {manualBuildings.length > 0 && (
          <div className="space-y-3 mb-4">
            {manualBuildings.map((building) => (
              <div
                key={building.id}
                className="p-5 rounded-lg border border-brand-primary bg-surface-selected"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold text-white">
                      {building.name && <>{building.name} — </>}
                      {building.address}, {building.postalCode} {building.city}
                      {building.countryThreeLetterCode && building.countryThreeLetterCode !== 'NOR' && (
                        <> ({building.countryThreeLetterCode})</>
                      )}
                    </div>
                    <div className="text-white/50 text-sm mt-1 flex flex-wrap gap-3">
                      {building.bra > 0 && <span>BRA: {building.bra.toLocaleString('nb-NO')} m²</span>}
                      {building.energyLabel && <span>Energi: {building.energyLabel}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-3 py-1 rounded bg-surface-elevated text-white/70 font-medium">
                      {BUILDING_TYPE_LABELS[building.type] ?? building.type}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveManual(building.id)}
                      className="p-1.5 text-white/40 hover:text-red-400 transition-colors"
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowManualForm(true)}
          className="w-full py-4 rounded-lg border-2 border-dashed border-white/20 text-white/60 hover:border-brand-primary hover:text-brand-primary transition-colors flex items-center justify-center gap-2 text-sm font-medium"
        >
          <FaPlus size={12} />
          {t('onboarding.buildings.registerManually')}
        </button>
      </div>}

      {showManualForm && (
        <BuildingForm
          open={showManualForm}
          title={t('onboarding.buildings.registerTitle')}
          onSubmit={handleManualBuildingSubmit}
          onClose={() => setShowManualForm(false)}
          hidePackages
          showCadastralFields
        />
      )}

      <OnboardingActions
        onBack={onBack}
        onNext={() => {
          if (isMatrikkelGrouped) {
            const selected = rawSearchResults.filter((b) => b.selected);
            const unselected = rawSearchResults.filter((b) => !b.selected);
            const grouped = groupBuildingsByMatrikkel(selected);
            onBuildingsChange([...grouped, ...unselected, ...manualBuildings]);
          }
          onNext();
        }}
        nextDisabled={selectedCount === 0}
      />
    </div>
  );
}
